import type {
  AudioTelemetry as ModelAudioTelemetry,
  CRTVarPatch as ModelCRTVarPatch,
  EngineMode as ModelEngineMode,
  EngineStatus as ModelEngineStatus,
  MidiMapping as ModelMidiMapping,
  ModulationRoute as ModelModulationRoute,
  ModulationSource as ModelModulationSource,
  PadId as ModelPadId,
  ParameterId as ModelParameterId,
  RuntimeState as ModelRuntimeState,
  SequencerSettings as ModelSequencerSettings,
  SequencerStep as ModelSequencerStep,
  SourceFit as ModelSourceFit,
  SourceKind as ModelSourceKind,
  Waveform as ModelWaveform,
} from "../model/types";

export const ENGINE_WIDTH = 640;
export const ENGINE_HEIGHT = 480;
export const MAX_VOICES = 4;
export const MAX_MODULATION_ROUTES = 8;

export type EngineState = ModelEngineStatus;
export type RenderMode = ModelEngineMode;
export type FitMode = ModelSourceFit;
export type SourceKind = ModelSourceKind;
export type PerformancePadId = ModelPadId;

export type NoteOwner = `keyboard:${string}` | `pointer:${number}` | `midi:${string}` | `sequencer:${number}` | string;

export type OscillatorWave = ModelWaveform;

export interface SynthParameters {
  osc1Wave: OscillatorWave;
  osc2Wave: OscillatorWave;
  osc1Octave: number;
  osc2Octave: number;
  osc1Detune: number;
  osc2Detune: number;
  osc1Level: number;
  osc2Level: number;
  oscMix: number;
  noise: number;
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  filterCutoff: number;
  filterResonance: number;
  drive: number;
  masterGain: number;
  lfo1Rate: number;
  lfo2Rate: number;
  lfo1Wave: OscillatorWave;
  lfo2Wave: OscillatorWave;
}

export interface VideoParameters {
  horizontalTear: number;
  verticalRoll: number;
  lineShift: number;
  dropout: number;
  chromaDisplacement: number;
  chromaPhase: number;
  chromaGain: number;
  desaturation: number;
  snow: number;
  impulses: number;
  brightnessBars: number;
  clipping: number;
  solarize: number;
  audioReactive: number;
  feedback: number;
  syncDistortion: number;
  burstDistortion: number;
  burstGain: number;
  linePosition: number;
  lineWidth: number;
  gain: number;
  dcOffset: number;
}

export const DEFAULT_SYNTH_PARAMETERS: SynthParameters = {
  osc1Wave: "saw",
  osc2Wave: "square",
  osc1Octave: 0,
  osc2Octave: -1,
  osc1Detune: -4,
  osc2Detune: 4,
  osc1Level: 0.72,
  osc2Level: 0.58,
  oscMix: 0.5,
  noise: 0.03,
  attack: 0.01,
  decay: 0.18,
  sustain: 0.72,
  release: 0.32,
  filterCutoff: 0.62,
  filterResonance: 0.15,
  drive: 0.2,
  masterGain: 0.55,
  lfo1Rate: 0.7,
  lfo2Rate: 0.17,
  lfo1Wave: "sine",
  lfo2Wave: "triangle",
};

export const DEFAULT_VIDEO_PARAMETERS: VideoParameters = {
  horizontalTear: 0.05,
  verticalRoll: 0,
  lineShift: 0.03,
  dropout: 0,
  chromaDisplacement: 0.02,
  chromaPhase: 0,
  chromaGain: 1,
  desaturation: 0,
  snow: 0.025,
  impulses: 0,
  brightnessBars: 0,
  clipping: 0,
  solarize: 0,
  audioReactive: 0.15,
  feedback: 0,
  syncDistortion: 0,
  burstDistortion: 0,
  burstGain: 1,
  linePosition: 0.48,
  lineWidth: 0.05,
  gain: 1,
  dcOffset: 0,
};

export type ModulationSource = ModelModulationSource;
export type ModulationTarget = ModelParameterId;
export type EngineModulationRoute = ModelModulationRoute;
export type SequencerStep = ModelSequencerStep;
export type SequencerConfig = ModelSequencerSettings;

export interface ModulationSnapshot {
  sequence: number;
  sampleFrame: number;
  lfo1: number;
  lfo2: number;
  envelope: number;
  audioRms: number;
  audioPeak: number;
  audioLow: number;
  audioMid: number;
  audioHigh: number;
  notePitch: number;
  noteVelocity: number;
  noise: number;
  expression: number;
  sequencer: number;
}

export interface PadSnapshot {
  syncKick: number;
  burstKill: number;
  lineCrush: number;
  frameTear: number;
  whiteout: number;
  freeze: boolean;
  unlock: number;
  panic: boolean;
}

export interface EngineCapabilities {
  webgl2: boolean;
  audioWorklet: boolean;
  sharedArrayBuffer: boolean;
  camera: boolean;
  midi: boolean;
  recording: boolean;
}

export interface EngineDiagnostics {
  state: EngineState;
  capabilities: EngineCapabilities;
  renderMode: RenderMode;
  source: SourceKind;
  frame: number;
  renderFps: number;
  renderP95Ms: number;
  audioSampleFrame: number;
  audioRms: number;
  audioPeak: number;
  activeNotes: number;
  sharedClock: boolean;
  recordingBytes: number;
  contextLost: boolean;
  lastError?: string;
}

export interface EngineEventMap {
  statechange: { state: EngineState };
  diagnostics: EngineDiagnostics;
  sourcechange: { kind: SourceKind };
  sourceerror: { kind: SourceKind; error: Error };
  midichange: { inputs: readonly MidiInputSummary[] };
  recordingwarning: { bytes: number };
  recordingstopped: { blob: Blob; mimeType: string };
  contextlost: undefined;
  contextrestored: undefined;
  error: { error: Error };
}

export interface MidiInputSummary {
  id: string;
  name: string;
  manufacturer: string;
  state: string;
}

export type MidiCcMapping = ModelMidiMapping;

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  bytes: number;
  durationMs: number;
}

export interface SourceSelection {
  kind: SourceKind;
  fit?: FitMode;
  file?: File;
  deviceId?: string;
}

export interface EngineControllerOptions {
  canvas: HTMLCanvasElement;
  oscilloscopeCanvas?: HTMLCanvasElement;
  spectrumCanvas?: HTMLCanvasElement;
  imageScopeCanvas?: HTMLCanvasElement;
  width?: number;
  height?: number;
  workletUrl?: string;
  safeOutput?: boolean;
  onEvent?: <K extends keyof EngineEventMap>(type: K, detail: EngineEventMap[K]) => void;
}

export type CRTVarPatch = ModelCRTVarPatch;
export type EngineMode = ModelEngineMode;
export type EngineStatus = ModelEngineStatus;
export type MidiMapping = ModelMidiMapping;
export type ModulationRoute = ModelModulationRoute;
export type ParameterId = ModelParameterId;
export type PadId = ModelPadId;
export type AudioTelemetry = ModelAudioTelemetry;
export type RuntimeState = ModelRuntimeState;
export type SequencerSettings = ModelSequencerSettings;
export type SourceFit = ModelSourceFit;
export type Waveform = ModelWaveform;

export const clamp01 = (value: number): number => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
